import { httpsCallable } from 'firebase/functions';
import { isListNotEmpty, isNullOrUndefined, isObjectEmpty, isObjectNotEmpty, isStringEmpty } from '../utils/objectUtils';
import { functions } from '.';
import logger from '../utils/logger';
import { getErrorMessage } from '../utils/errorUtils';
import { enqueueProgressWrite, toRestoredDates } from './Leads';

// MUST stay above `recordweightchange`'s own `timeoutSeconds` (20s), for the reason the funnel callables
// in Leads.js record: a server budget longer than the client's leaves the abandoned execution alive after
// the client has given up, and the per-user chain has already released the next write against it
const RECORD_WEIGHT_CHANGE_TIMEOUT = 30000;

const HISTORY_DATE_FIELDS = ['at'];
const SNAPSHOT_DATE_FIELDS = ['startingWeightAt', 'currentWeightAt', 'previousWeightAt', 'lowestWeightAt', 'highestWeightAt'];

const nothingRecorded = () => ({ updatedFields: {}, recorded: false });

// the callable answers with the fields it wrote; a stored date crosses the boundary as a
// `{ _seconds, _nanoseconds }` map and the one it just minted as ISO text
const toRestoredWeightFields = (updatedFields) => {
    if (isObjectEmpty(updatedFields)) {
        return {};
    }

    const restored = { ...updatedFields };

    if (isListNotEmpty(restored.weightHistory)) {
        restored.weightHistory = restored.weightHistory.map((entry) => toRestoredDates(entry, HISTORY_DATE_FIELDS));
    }

    if (isObjectNotEmpty(restored.weightSnapshot)) {
        restored.weightSnapshot = toRestoredDates(restored.weightSnapshot, SNAPSHOT_DATE_FIELDS);
    }

    return restored;
};

/**
 * Records one weight against a profile, with the `bmi` and `height` computed from the same reading. The
 * server derives `weightHistory` and `weightSnapshot` from the stored document, records every submission
 * except the same value re-sent from the same source record, writes the `users/{userId}/weightHistory`
 * entry, and answers with the profile fields it wrote so a caller can merge them into its own copy. One
 * call, one commit - nothing here touches Firestore directly.
 *
 * Queued per user with the funnel's profile writes, so two readings in quick succession are recorded in
 * the order they were made and a reading never overtakes a profile write issued ahead of it.
 *
 * Answers `{ updatedFields: {}, recorded: false }` rather than throwing: the check-in, follow-up and
 * meal-plan pages call this mid-submission, after their own document is created, and a weight that
 * failed to record must not leave the customer on a spinner with the rest of their submission unsaved.
 * The failure is logged.
 */
export const recordWeightChange = async ({ userId, weight = null, bmi = null, height = null, source = null, sourceEntityId = null }) => {
    if (isStringEmpty(userId) || [weight, bmi, height].every((value) => isNullOrUndefined(value))) {
        return nothingRecorded();
    }

    const func = httpsCallable(functions, 'recordweightchange', { timeout: RECORD_WEIGHT_CHANGE_TIMEOUT });

    return await enqueueProgressWrite(userId, async () => {
        try {
            const result = await func({ userId, weight, bmi, height, source, sourceEntityId });

            return { updatedFields: toRestoredWeightFields(result?.data?.updatedFields), recorded: result?.data?.recorded === true };
        } catch (error) {
            logger.logErrorEvent('RECORD_WEIGHT_CHANGE', error, getErrorMessage(error), true);

            return nothingRecorded();
        }
    });
};
